import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import  useStickyState from './components/Core/StickyState';

// Firebase Auth - no OAuth provider wrapper needed
import useAuthStore from './stores/useAuthStore';
import { analyticsService } from './services/analytics.service';
import { firestoreService } from './services/firestore.service';
import { loadLinkedAccountsFromFirestore } from './services/auth.service';

// Zustand stores (migrated from Context)
import { useEventsStore } from './stores/useEventsStore';
import { useTasksStore } from './stores/useTasksStore';

import { DesignProvider } from "./components/Context/DesignContext";
import { basic_theme, tasks_json, user_settings, GrownupsContext }from "./GrownupsContext"; // TODO
import {applyAppColors} from "./components/Sidebar/SidebarDesign/SelectColorScheme";
import { CALENDAR_NAME } from './config/google-calendar';
import {setCurrentUserCalendarAndSelectionColors} from './components/Calendar/Utils/CalendarUtils';
import {applyAppFonts} from './components/Sidebar/SidebarDesign/SelectFonts';
import OnboardingGuard from './components/Onboarding/OnboardingGuard';
import { ToastContainer } from './components/Toast';
import OfflineIndicator from './components/UI/OfflineIndicator';
import LoadingScreen from './components/UI/LoadingScreen';
import { EventDetailModal } from './components/EventDetailModal';

const GrownupsCalendarRoutes = lazy(() => import('./Routes')); 


// const isLocalhost = Boolean(
//   window.location.hostname === "localhost" ||
//     // [::1] is the IPv6 localhost address.
//     window.location.hostname === "[::1]" ||
//     // 127.0.0.1/8 is considered localhost for IPv4.
//     window.location.hostname.match(
//       /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
//     )
// );

// Assuming you have two redirect URIs, and the first is for localhost and second is for production
// const [
//   localRedirectSignIn,
//   productionRedirectSignIn,
// ] = awsConfig.oauth.redirectSignIn.split(",");

// const [
//   localRedirectSignOut,
//   productionRedirectSignOut,
// ] = awsConfig.oauth.redirectSignOut.split(",");

// const updatedAwsConfig = {
//   ...awsConfig,
//   oauth: {
//     ...awsConfig.oauth,
//     redirectSignIn: isLocalhost ? localRedirectSignIn : productionRedirectSignIn,
//     redirectSignOut: isLocalhost ? localRedirectSignOut : productionRedirectSignOut,
//   }
// }


// Amplify.configure(awsConfig);


// function App({ signOut, user }) {

// Hide the HTML loading screen once React app is ready
// Exported so lazy-loaded components can call it when they're ready to render
// Uses requestAnimationFrame to ensure the browser has painted the new content
export const hideInitialLoader = (options = {}) => {
  const { minDelay = 300 } = options; // Minimum delay to prevent jarring flash

  const loader = document.getElementById('initial-loader');
  if (!loader || loader.classList.contains('hidden')) return;

  // Use double requestAnimationFrame to ensure paint has occurred
  // First rAF schedules for next frame, second ensures previous frame completed
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Add minimum delay to ensure smooth transition
      setTimeout(() => {
        if (loader && !loader.classList.contains('hidden')) {
          loader.classList.add('hidden');
          setTimeout(() => loader.remove(), 500);
        }
      }, minDelay);
    });
  });
};

// Hook for components to use - waits for DOM to be ready before hiding loader
export const useHideLoader = (isReady = true) => {
  React.useEffect(() => {
    if (isReady) {
      hideInitialLoader();
    }
  }, [isReady]);
};

function App() {
  // Note: We no longer hide the initial loader here.
  // Instead, it's hidden by the first route component that renders.
  // This prevents blank screen flashes while lazy-loaded routes are loading.

  // Events and Tasks now managed by Zustand stores (GRO-19, GRO-20)
  const events = useEventsStore((state) => state.events);
  const setEvents = useEventsStore((state) => state.setEvents);
  const taskLists = useTasksStore((state) => state.taskLists);
  const getListsNamesByID = useTasksStore((state) => state.getListsNamesByID);
  const [colors, setColors] = useStickyState('basic', 'colors')

  const [draggedEvent, setDraggedEvent] = useState(null)

  const [font, setFont] = useStickyState(basic_theme.font, 'font'); // TODO - not sure if using it
  const [fontSize, setFontSize] = useStickyState(basic_theme.fontSize, 'fontSize');
  const [fonts, setFonts] = useStickyState(basic_theme.fontsInitial, 'fonts');

  const [backgroundColor, setBackgroundColor] = useStickyState(basic_theme.backgroundColor, 'backgroundColor');
  const [backgroundColorCalendar, setBackgroundColorCalendar] = useStickyState(basic_theme.backgroundColorCalendar, 'backgroundColorCalendar');
  
  const [activeSideBarItem, setActiveSideBarItem] = useStickyState('tasks', 'tasks')
  const [designMode, setDesignMode] = useState(activeSideBarItem==='design')

  // Calendar providers (populated from connected accounts)
  const [calendars, setCalendars] = useState([]);
  const [calendarsByKeys, setCalendarByKeys] = useState({});
  // Connected accounts (synced with Firebase auth)
  const [accounts, setAccounts] = useState([]);
  // dict of checked/unchecked calendars
  const [selectedCalendars, setSelectedCalendars] = useStickyState({}, 'selectedCalendars'); //useStickyState([], 'calendars'); top level calendar providers
  // currently user selected calendar (to create events belonging to it)
  const [currentUserCalendar, setCurrentUserCalendar] = useStickyState(null, 'currentUserCalendar');

  const [userCalendarSettings, setUserCalendarSettings] = useStickyState(user_settings, 'userCalendarSettings');

  // Experiences counter for activities
  const [experiencesCount, setExperiencesCount] = useStickyState(0, 'experiencesCount');

  // Preview calendar for themed calendars feature
  const [previewCalendar, setPreviewCalendar] = useState(null);
  // Callback for adding preview events (set by CalendarsSection)
  const [onAddPreviewEventCallback, setOnAddPreviewEventCallback] = useState(null);

  useEffect(()=>{
      applyAppColors(colors, setBackgroundColor, setBackgroundColorCalendar, setFonts)
  }, [colors])

  useEffect(()=>{
    applyAppFonts(fonts)
  }, [fonts])

  useEffect(() => {
    let calByKeys = {}
    let firstCalendar = null;
    let firstCalendarIdentifierWithDivider = null;
    let grownupsCalendar = null;
    let grownupsCalendarIdentifier = null;
    for (const account of calendars) {
      for (const cal of account.calendars) {
        const calAccountIdentifierWithDivider = `${account.email}-${cal.id}`;
        const calUpdated = { ...cal, primary_email: account.email };
        calByKeys[calAccountIdentifierWithDivider] = calUpdated

        // Select first writable calendar as default (owner or writer access)
        const isWritable = cal.accessRole === 'owner' || cal.accessRole === 'writer';
        if (!firstCalendar && isWritable) {
          firstCalendar = calUpdated
          firstCalendarIdentifierWithDivider = calAccountIdentifierWithDivider;
        }
        // Prefer a dedicated "grownupscalendar" calendar (not the primary/email calendar)
        if (!grownupsCalendar && isWritable && cal.summary &&
            cal.summary.toLowerCase().includes('grownupscalendar') &&
            cal.id !== account.email) {
          grownupsCalendar = calUpdated;
          grownupsCalendarIdentifier = calAccountIdentifierWithDivider;
        }
      }
    }
    setCalendarByKeys(calByKeys);

    // Check if current selection is the primary email calendar (old default)
    const isPrimaryEmailCalendar = currentUserCalendar &&
        calendars.some(account => currentUserCalendar === `${account.email}-${account.email}`);

    if (!currentUserCalendar || (grownupsCalendar && isPrimaryEmailCalendar)) {
      // Prefer grownupscalendar, fall back to first writable
      const defaultCal = grownupsCalendar || firstCalendar;
      const defaultKey = grownupsCalendarIdentifier || firstCalendarIdentifierWithDivider;
      if (defaultKey && defaultCal) {
        setCurrentUserCalendarAndSelectionColors(setCurrentUserCalendar, defaultKey, defaultCal)
      }
    }

  }, [calendars])

  // Sync accounts with Firebase auth state
  const currentUser = useAuthStore((state) => state.currentUser);
  useEffect(() => {
    if (currentUser) {
      // User is logged in - set primary account from Firebase user data
      const primaryAccount = {
        provider: 'google',
        key: currentUser.email,
        email: currentUser.email,
        name: currentUser.displayName || currentUser.email,
        given_name: currentUser.displayName?.split(' ')[0] || '',
        family_name: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
        picture: currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.displayName || currentUser.email)}&background=random`,
      };
      setAccounts([primaryAccount]);

      // Load linked accounts from Firestore and merge into accounts
      loadLinkedAccountsFromFirestore().then(linkedAccounts => {
        const additional = linkedAccounts
          .filter(la => la.email !== currentUser.email)
          .map(la => ({
            provider: 'google',
            key: la.email,
            email: la.email,
            name: la.displayName || la.email,
            given_name: la.displayName?.split(' ')[0] || '',
            family_name: la.displayName?.split(' ').slice(1).join(' ') || '',
            picture: la.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(la.displayName || la.email)}&background=random`,
          }));
        if (additional.length > 0) {
          setAccounts(prev => [...prev, ...additional]);
        }
      }).catch(() => {
        // Non-blocking — primary account is already set
      });
    } else {
      // User is logged out - clear accounts
      setAccounts([]);
    }
  }, [currentUser]);

  // Count experiences (activities) from all events
  // Activities have type === 'activity' regardless of which calendar they're in
  useEffect(() => {
    if (!currentUser) return;

    // Count all events with type === 'activity'
    const count = events.filter(event => event.type === 'activity').length;

    // Only update if count differs (to avoid infinite loops)
    if (count !== experiencesCount) {
      setExperiencesCount(count);
    }
  }, [events, currentUser]);

  // Sync experiencesCount to Firestore whenever it changes
  const experiencesCountRef = React.useRef(experiencesCount);
  useEffect(() => {
    // Skip initial render and only sync when experiencesCount actually changes
    if (!currentUser || experiencesCountRef.current === experiencesCount) {
      experiencesCountRef.current = experiencesCount;
      return;
    }
    experiencesCountRef.current = experiencesCount;

    // Sync to Firestore
    firestoreService.setExperiencesCount(currentUser.uid, experiencesCount).catch(error => {
      console.error('Failed to sync experiencesCount to Firestore:', error);
    });
  }, [currentUser, experiencesCount]);

  // TODO - re-render
  // useEffect(()=> {setTaskListsNamesbyID(getListsNamesByID(taskLists))}, [taskLists]) // TODO: not efficient

  return (<>
    {/* <Authenticator>
      {({ signOut, user }) => ( */}

    <HelmetProvider>
    <Suspense fallback={<LoadingScreen />}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Grownups Calendar</title>
                <link rel="canonical" href="http://grownupscalendar.com" />
                <meta name="description" content="Fun Calendar for Grownups" />

            </Helmet>

        {/* Firebase Auth - no wrapper needed, auth state managed by useAuthStore */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>

        <GrownupsContext.Provider value={{ font, fontSize, setFont, setFontSize, backgroundColor, setBackgroundColor, backgroundColorCalendar, setBackgroundColorCalendar,
                                          activeSideBarItem, setActiveSideBarItem, calendars, setCalendars,
                                          userCalendarSettings, setUserCalendarSettings,
                                          // taskLists now accessed via useTasksStore (GRO-20)
                                          accounts, setAccounts,
                                          selectedCalendars, setSelectedCalendars,
                                          calendarsByKeys,
                                          currentUserCalendar, setCurrentUserCalendar,
                                          fonts, setFonts,
                                          draggedEvent, setDraggedEvent, colors, setColors,
                                          experiencesCount, setExperiencesCount,
                                          previewCalendar, setPreviewCalendar,
                                          onAddPreviewEventCallback, setOnAddPreviewEventCallback,}}>

        {/* EventsContext removed - use useEventsStore instead (GRO-19) */}
        <DesignProvider>
        {/* Height 45px */}
        {/* <div style={{paddingBottom: '65px', backgroundColor: 'whitesmoke'}}> */}
          <div>
            <OnboardingGuard>
              <GrownupsCalendarRoutes backgroundColor={backgroundColor} backgroundColorCalendar={backgroundColorCalendar} activeSideBarItem={activeSideBarItem} setActiveSideBarItem={setActiveSideBarItem}
                     designMode={designMode} setDesignMode={setDesignMode}/>
            </OnboardingGuard>
          </div>
        {/* Event Detail Modal (for themed calendar events) - must be inside GrownupsContext */}
        <EventDetailModal />
        </DesignProvider>
        </GrownupsContext.Provider>
        {/* <div style={{backgroundColor: 'pink'}}>Bla</div> */}
        </BrowserRouter>
    </Suspense>
    </HelmetProvider>
    {/* Toast notifications (GRO-35) */}
    <ToastContainer />
    {/* Offline indicator (GRO-32) */}
    <OfflineIndicator />
      {/* )}
      </Authenticator> */}
    </>
    );
}

export default App;
